// Exports the whole collage to a high-resolution PNG.
//
// DELIBERATELY DOES NOT DO "SVG -> serialization -> Image -> canvas" (the
// original approach, inherited from the prototype) - that exact path was
// repeatedly caught silently producing an empty/transparent output with no
// photos on real Safari (Mac), even though the live preview in the editor
// (the same SVG, directly in the DOM) is always visually correct. Instead,
// export here draws directly from a description of the current collage
// state (ShapemoryEditor.getExportModel()) using plain Canvas 2D commands
// (clip + drawImage), completely bypassing any SVG-as-image rasterization
// and with it the whole category of its known Safari inconsistencies
// (xlink:href, SVG filters on <image>, blob-URL <img> "load" vs. actual
// paint).

function loadRasterImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load the photo for export'));
    img.src = src;
  });
}

// Converts a piece's source shape (<path> or <polygon>, exactly as in the
// original SVG file) into a path string usable in Path2D.
function shapeToPathString(shapeEl) {
  const tag = shapeEl.tagName.toLowerCase();
  if (tag === 'path') return shapeEl.getAttribute('d') || '';
  if (tag === 'polygon' || tag === 'polyline') {
    const nums = (shapeEl.getAttribute('points').match(/-?\d*\.?\d+(?:e-?\d+)?/gi) || []).map(Number);
    let d = '';
    for (let i = 0; i + 1 < nums.length; i += 2) {
      d += (i === 0 ? 'M ' : 'L ') + nums[i] + ',' + nums[i + 1] + ' ';
    }
    return d + 'Z';
  }
  return '';
}

// "linked" pieces have multiple shapes sharing one photo (e.g. a paw) -
// merging multiple paths into one Path2D works as a classic "union" clip,
// exactly like several children in one <clipPath> in the original SVG
// approach.
function buildClipPath(shapes) {
  const path = new Path2D();
  shapes.forEach((shapeEl) => {
    path.addPath(new Path2D(shapeToPathString(shapeEl)));
  });
  return path;
}

function clampByte(v) {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

// Draws one photo piece (editable/linked) onto the main canvas.
// Steps:
//   1. the photo is drawn, with exactly the same transform (pan/rotation/
//      zoom) it has live in the editor, into a small helper canvas cropped
//      to just the piece's bounding-box area (for performance - no need to
//      process the whole 3000px canvas for one piece),
//   2. if the piece has a brightness/contrast adjustment set (other than
//      "unchanged"), the same linear channel recompute that SVG's
//      feComponentTransfer does live (slope/intercept) is applied, but
//      directly on the pixel data - no dependency on SVG filters anywhere
//      in the export path,
//   3. the result, clipped to the piece's actual shape (Path2D clip), is
//      drawn onto the main canvas.
async function drawPiece(ctx, piece, scale) {
  const img = await loadRasterImage(piece.imageDataUrl);

  const bbox = piece.bbox;
  const pieceCanvas = document.createElement('canvas');
  pieceCanvas.width = Math.max(1, Math.ceil(bbox.width * scale));
  pieceCanvas.height = Math.max(1, Math.ceil(bbox.height * scale));
  const pctx = pieceCanvas.getContext('2d');

  pctx.translate(-bbox.x * scale, -bbox.y * scale);
  pctx.scale(scale, scale);
  const t = piece.transform;
  pctx.translate(t.panX, t.panY);
  pctx.translate(t.imgCenterX, t.imgCenterY);
  pctx.rotate((t.rotation * Math.PI) / 180);
  pctx.scale(t.scale, t.scale);
  pctx.translate(-t.imgCenterX, -t.imgCenterY);
  pctx.drawImage(img, piece.imageX, piece.imageY, piece.imageW, piece.imageH);

  const isNoopFilter = piece.filter.slope === 1 && piece.filter.intercept === 0;
  if (!isNoopFilter) {
    const imageData = pctx.getImageData(0, 0, pieceCanvas.width, pieceCanvas.height);
    const d = imageData.data;
    const slope = piece.filter.slope;
    const intercept = piece.filter.intercept * 255;
    for (let i = 0; i < d.length; i += 4) {
      d[i] = clampByte(d[i] * slope + intercept);
      d[i + 1] = clampByte(d[i + 1] * slope + intercept);
      d[i + 2] = clampByte(d[i + 2] * slope + intercept);
    }
    pctx.putImageData(imageData, 0, 0);
  }

  ctx.save();
  ctx.clip(buildClipPath(piece.shapes));
  ctx.drawImage(pieceCanvas, bbox.x, bbox.y, bbox.width, bbox.height);
  ctx.restore();
}

async function exportCollageAsPng(model, targetWidth, grayscale) {
  const [, , vw, vh] = model.viewBox;
  const scale = targetWidth / vw;
  const targetHeight = Math.round(targetWidth * (vh / vw));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);

  for (const item of model.items) {
    if (item.kind === 'fixed') {
      ctx.save();
      ctx.fillStyle = '#000';
      ctx.fill(new Path2D(shapeToPathString(item.shape)));
      ctx.restore();
      continue;
    }
    if (!item.hasImage) continue;
    await drawPiece(ctx, item, scale);
  }

  if (grayscale) {
    // An actual pixel recompute (not just a visual CSS filter), so the
    // uploaded PNG is genuinely black-and-white regardless of browser.
    const imageData = ctx.getImageData(0, 0, targetWidth, targetHeight);
    const d = imageData.data;
    for (let i = 0; i < d.length; i += 4) {
      const gray = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
      d[i] = d[i + 1] = d[i + 2] = gray;
    }
    ctx.putImageData(imageData, 0, 0);
  }

  const pngBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  const pngUrl = URL.createObjectURL(pngBlob);
  return { url: pngUrl, blob: pngBlob, width: targetWidth, height: targetHeight };
}
